import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { archiveService } from "../../api/service.ts";
import { useIsMobile } from "../../hooks/useIsMobile.ts";

interface Props {
    open: boolean;
    onClose: () => void;
    /** chiamata a fine caricamento se almeno una foto è stata aggiunta */
    onUploaded: () => void;
}

type Outcome = { name: string; status: "added" | "duplicate" | "error"; message?: string };

function errorText(err: any): string {
    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (err?.response?.status === 413) return "File troppo grande (max 20 MB)";
    return data?.message || err?.message || "Errore";
}

/** Caricamento di una o più foto nell'archivio storico (solo ADMIN) */
export default function ArchiveUploadDialog({ open, onClose, onUploaded }: Props) {
    const isMobile = useIsMobile();
    const [files, setFiles] = useState<File[]>([]);
    const [year, setYear] = useState<string>(String(new Date().getFullYear()));
    const [date, setDate] = useState<string>("");
    const [season, setSeason] = useState<string>("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(0);
    const [outcomes, setOutcomes] = useState<Outcome[]>([]);

    useEffect(() => {
        if (open) {
            setFiles([]);
            setDate("");
            setSeason("");
            setDone(0);
            setOutcomes([]);
        }
    }, [open]);

    const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
    useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

    const y = Number(year);
    const yearValid = Number.isInteger(y) && y >= 2000 && y <= 2100;
    const seasonOptions = yearValid ? [`${y - 1}/${y}`, `${y}/${y + 1}`] : [];
    // con la data precisa la stagione si ricava da sola; con solo l'anno va scelta
    const needsSeason = !date;
    const valid = files.length > 0 && (date ? true : yearValid && seasonOptions.includes(season));

    const upload = async () => {
        setBusy(true);
        const results: Outcome[] = [];
        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            try {
                const r = await archiveService.upload(f, date ? { date } : { year: y, season });
                results.push({ name: f.name, status: r.duplicate ? "duplicate" : "added" });
            } catch (e) {
                results.push({ name: f.name, status: "error", message: errorText(e) });
            }
            setDone(i + 1);
            setOutcomes([...results]);
        }
        setBusy(false);
        if (results.some((r) => r.status === "added")) onUploaded();
    };

    const added = outcomes.filter((o) => o.status === "added").length;
    const dup = outcomes.filter((o) => o.status === "duplicate").length;
    const errors = outcomes.filter((o) => o.status === "error");
    const finished = !busy && outcomes.length > 0;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
            <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>Aggiungi foto all'archivio</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Button component="label" variant="outlined" startIcon={<AddPhotoAlternateIcon />} disabled={busy}>
                        {files.length ? `${files.length} foto selezionate — cambia` : "Scegli le foto"}
                        <input
                            hidden
                            type="file"
                            accept="image/jpeg,image/png"
                            multiple
                            onChange={(e) => {
                                setFiles(Array.from(e.target.files ?? []));
                                setOutcomes([]);
                                setDone(0);
                            }}
                        />
                    </Button>

                    {files.length > 0 && (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 0.5 }}>
                            {previews.map((src, i) => (
                                <Box key={src} component="img" src={src} alt={files[i].name}
                                    sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 1 }} />
                            ))}
                        </Box>
                    )}

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Anno"
                            type="number"
                            value={year}
                            onChange={(e) => { setYear(e.target.value); setSeason(""); }}
                            disabled={busy || !!date}
                            error={!date && !yearValid}
                            sx={{ width: 120 }}
                        />
                        <TextField
                            label="Data precisa (facoltativa)"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            disabled={busy}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>

                    {needsSeason && yearValid && (
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Con solo l'anno indica la stagione (da settembre ad agosto):
                            </Typography>
                            <RadioGroup row value={season} onChange={(e) => setSeason(e.target.value)}>
                                {seasonOptions.map((s) => (
                                    <FormControlLabel key={s} value={s} control={<Radio />} label={s} disabled={busy} />
                                ))}
                            </RadioGroup>
                        </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                        Se una foto ha la data di scatto nei metadati e cade nell'anno indicato, viene usata quella.
                        Posizione GPS e altri metadati vengono rimossi.
                    </Typography>

                    {(busy || finished) && (
                        <Box>
                            <LinearProgress variant="determinate" value={(done / files.length) * 100} />
                            <Typography variant="caption">{done} / {files.length}</Typography>
                        </Box>
                    )}

                    {finished && (
                        <Alert severity={errors.length ? "warning" : "success"}>
                            {added} aggiunte{dup ? `, ${dup} già presenti` : ""}{errors.length ? `, ${errors.length} con errore` : ""}
                            {errors.map((e) => (
                                <div key={e.name}><strong>{e.name}</strong>: {e.message}</div>
                            ))}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{finished ? "Chiudi" : "Annulla"}</Button>
                {!finished && (
                    <Button variant="contained" onClick={upload} disabled={!valid || busy}>
                        Carica {files.length || ""} foto
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
